import React, { useEffect, useRef, useState } from "react";
import { Box, Dialog, DialogContent } from "@material-ui/core";
import Select from "react-select";
import chroma from "chroma-js";
import COLORS from "scss/colors.module.scss";

import {
    ColumnName,
    SavingSpinnerForColumnEditors,
    ActionButtonsForColumnEditors,
    handleClose,
    ErrorMessageViewer,
    checkValuesUpdation
} from "./ColumnEditorFactory";
import { validateDropdownInputsBeforeSaving } from "../RowId/Validations";
import { OptionFormatter } from "../Multiselect/MultiselectColumnEditor";

const MultiSelectReusableEditor = ({
    initialValue = "",
    open,
    data,
    column,
    columnName,
    onSave,
    onCancel,
}) => {
    const [options, setOptions] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [validationError, setValidationError] = useState({ isInvalid: false, message: "" });
    const [isSaving, setIsSaving] = useState(false);
    const selectRef = useRef(null);

    const columnOptions = column?.normalizedColumnOptions ?? {};

    useEffect(() => {
        if (!open) return;

        const opts = (columnOptions.options || []).map((opt, idx) => ({
            value: typeof opt === "string" ? opt.trim() : opt,
            label: typeof opt === "string" ? opt.trim() : opt,
            color: COLORS.PRIMARY_COLOR,
            background: columnOptions.colors?.[idx] || "grey",
        }));
        setOptions(opts);

        const initVals = initialValue
            ? initialValue.split(",").map((v) => (String(v).trim()))
            : [];

        const selected = initVals.map((val) => {
            const found = opts.find((o) => o.value === val);
            return (
                found || { value: val, label: val, color: COLORS.PRIMARY_COLOR, background: "grey" }
            );
        });
        setSelectedOptions(selected);

        requestAnimationFrame(() => {
            const inst = selectRef.current;
            if (!inst) return;
            if (typeof inst.focus === "function") inst.focus();
            else if (inst.select && typeof inst.select.focus === "function") {
                inst.select.focus();
            }
        });
        return () => {
            setValidationError({ isInvalid: false, message: "" });
        }
    }, [open, initialValue]);

    const getSelectedValuesInOrder = () => {
        const selectedValues = selectedOptions.map(o => o.value);
        if (checkValuesUpdation(initialValue, selectedValues)) {
            return selectedValues.join(", ");
        } else {
            return initialValue;
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const selectedOptionsValues = selectedOptions.map(o => o.value);
            if (!checkValuesUpdation(initialValue, selectedOptionsValues)) {
                onCancel();
                return;
            }

            const value = selectedOptionsValues.join(", ");
            const isUpdateAllowed = validateDropdownInputsBeforeSaving(value, column, data);
            if (isUpdateAllowed.isInvalid) {
                setValidationError(isUpdateAllowed);
                return;
            }

            await onSave(value, initialValue, column);
        } catch (e) {
            console.error("Error saving multiselect value:", e);
        } finally {
            setIsSaving(false);
        }
    };

    const selectStyles = {
        control: (provided) => ({
            ...provided,
            minWidth: "100%",
            marginTop: 8,
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 2000,
        }),
        menu: (base) => ({
            ...base,
            zIndex: 2000,
            maxHeight: "250px",
            overflowY: "auto",
            boxShadow: "inset 0 1px 0 rgba(0, 0, 0, 0.1)",
        }),
        option: (styles, { data, isDisabled, isFocused, isSelected }) => {
            const background =
                data.background === "transparent" || Array.isArray(data.background)
                ? chroma("grey")
                : chroma(data.background);

            return {
                ...styles,
                minHeight: 32,
                display: "flex",
                alignItems: "center",
                backgroundColor: isDisabled
                ? null
                : isSelected
                ? data.background
                : isFocused
                ? background.alpha(0.1).css()
                : null,
                color: isDisabled
                ? COLORS.DISABLED_MENU_OPTION
                : isSelected
                ? chroma.contrast(background, COLORS.PRIMARY_COLOR) > 2
                    ? COLORS.PRIMARY_COLOR
                    : COLORS.SECONDARY_COLOR
                : data.background,
                cursor: isDisabled ? "not-allowed" : "default",
                ":active": {
                ...styles[":active"],
                backgroundColor:
                    !isDisabled &&
                    (isSelected ? data.background : background.alpha(0.3).css()),
                },
            };
        },
        multiValue: (styles, { data }) => ({
            ...styles,
            backgroundColor: data.background,
            borderRadius: 20,
            padding: 3,
        }),
        multiValueLabel: (styles) => ({
            ...styles,
            color: COLORS.PRIMARY_COLOR,
        }),
        multiValueRemove: (styles, { data }) => {
            const bg = chroma(COLORS.PRIMARY_COLOR);
            return {
                ...styles,
                color: data.background,
                backgroundColor: bg.alpha(0.7).css(),
                cursor: "pointer",
                borderRadius: 20,
                padding: 0,
                height: 16,
                width: 16,
                alignSelf: "center",
                justifyContent: "center",
                ":hover": {
                backgroundColor: COLORS.PRIMARY_COLOR,
                },
            };
        },
    };

    return (
        <Dialog
            open={open}
            onClose={() => handleClose(getSelectedValuesInOrder(), initialValue, onCancel, validationError, setValidationError)}
            fullWidth
            PaperProps={{
                style: {
                    width: validationError.isInvalid ? "450px" : "420px",
                    minHeight: validationError.isInvalid ? "200px" : "180px",
                    maxHeight: "500px",
                    borderRadius: "8px",
                },
            }}
        >
            <DialogContent>
                <Box display="flex" flexDirection="column" style={{ width: "100%", gap: 8 }}>
                    {columnName && <ColumnName columnName={columnName} />}

                    <Select
                        ref={selectRef}
                        value={selectedOptions}
                        onChange={(vals) => setSelectedOptions(vals || [])}
                        options={options}
                        isMulti={true}
                        closeMenuOnSelect={false}
                        backspaceRemovesValue
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                        placeholder="Find options..."
                        styles={selectStyles}
                        components={{
                            DropdownIndicator: null,
                            IndicatorSeparator: null,
                        }}
                        formatOptionLabel={(props) => (
                            <OptionFormatter {...props} columnWidth={validationError.isInvalid ? "402px" : "372px"}/>
                        )}
                        maxMenuHeight={150}
                    />

                    {validationError.isInvalid && (<ErrorMessageViewer errMsg={validationError.message} />)}

                    <ActionButtonsForColumnEditors
                        handleSave={handleSave}
                        handleCancel={() => handleClose(getSelectedValuesInOrder(), initialValue, onCancel, validationError, setValidationError)}
                        isSaveDisabled={isSaving}
                        isCloseDisabled={isSaving}
                    />

                    {isSaving && <SavingSpinnerForColumnEditors />}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default MultiSelectReusableEditor;
